import * as React from 'react'
import { Text, View, TouchableOpacity } from 'react-native'
import styled from '@theme/styled-components'

import Root from '@components/root/Root'
import Header from '@components/header/Header'
import Tabs from '@components/tabs/Tabs'
import SegmentedControl from '@components/segmentedControl/SegmentedControl'
import Loader from '@components/loader/Loader'
import InfiniteList from '@components/list/InfiniteList'
import ControleCard from '@components/card/ControleCard'
import ControleFilters from './filters/ControleFilters'
import Drawer from '@components/drawer/Drawer'
import SortSelector from '@components/modal/SortSelector'

import useI18n from '@i18n/useI18n'
import useNavigation from '@layout/useNavigation'
import useBreakpoint from '@utils/useBreakpoints'

import useReducer from '@store/useReducer'
import * as FiltreStore from '@store/filtre/filtre'
import * as SiteStore from '@store/site/site'
import * as UserStore from '@store/user/user'
import * as SocieteStore from '@store/societe/societe'
import * as LocationStore from '@store/location/location'

import api from '@api/api'

import { ControleQualiteNature } from '@api/enum'

import Logger from '@utils/logger'
import { format } from 'date-fns'

import { MODES } from './detail/utils'
import { paramsToSearch } from '@api/utils'

import { createEvent, createStore } from 'effector'

export const setResults = createEvent<ControleQualiteResponse[]>('setControleResults')

export const ResultStore = createStore<ControleQualiteResponse[]>([], { name: 'controleResult' }).on(
  setResults,
  (s, payload) => payload
)

interface Props {
  manifestationId?: string
  metierId?: string
  societeId?: string
  espaceId?: string
  observation?: boolean
  conforme?: boolean
  nature?: ControleQualiteNature
}

const TABS = [ControleQualiteNature.PRESTATION, ControleQualiteNature.MAINTENANCE] as const
const SORTS = ['creationDate', 'scoreConformitePourcent'] as const

const ITEM_WIDTH = 375
const ITEM_HEIGHT = 150
const DRAWER_WIDTH = 300

const INITIAL_FILTERS: ControleFilters = {
  manifestation: [],
  metier: [],
  societe: [],
  period: 'all',
}

const DISPLAYS = [
  'manifestation',
  'metier',
  'societe',
  'period',
  'type',
  'from',
  'to',
  'observation',
  'conforme',
] as const

const ControleHistoryListScreen = ({
  manifestationId,
  metierId,
  societeId,
  espaceId,
  observation,
  conforme,
  nature,
}: Props) => {
  const i18n = useI18n()
  const navigation = useNavigation()
  const [{ width }, bp] = useBreakpoint()
  const isPhone = bp === 'phone'

  const initial = {
    ...INITIAL_FILTERS,
    manifestation: manifestationId ? [manifestationId] : [],
    metier: metierId ? [metierId] : [],
    societe: societeId ? [societeId] : [],
    observation,
    conforme,
    nature,
  }

  const controles = useReducer(ResultStore, (s) => s)
  const site = useReducer(SiteStore.store, (s) => s.site)
  const user = useReducer(UserStore.store, (s) => s.user)

  const services = useReducer(SocieteStore.store, (s) => s.services)
  const places = useReducer(LocationStore.store, (s) => s.places)
  const manifestations = useReducer(SiteStore.store, (s) => s.manifestations)

  const societe = React.useMemo(() => societeId && services.find((s) => s.id === societeId), [services, societeId])
  const espace = React.useMemo(() => espaceId && places.find((s) => s.id === espaceId), [places, espaceId])
  const manifestation = React.useMemo(
    () =>
      manifestationId &&
      [...manifestations.inProgress, ...manifestations.incoming, ...manifestations.toBeClosed].find(
        (s) => s.id === manifestationId
      ),
    [manifestations, manifestationId]
  )

  const [tab, setTab] = React.useState<ControleQualiteNature>(nature || ControleQualiteNature.PRESTATION)
  const [sort, setSort] = React.useState(SORTS[0])
  const [reverse, setReverse] = React.useState(false)
  const [page, setPage] = React.useState(0)
  const [pagination, setPagination] = React.useState<ControleQualitePageResponse>()
  const [loadingData, setLoadingData] = React.useState(false)
  const [filters, setFilters] = React.useState<ControleFilters>(initial)

  const mySociete = !!user && !!user.societeId && user.societeId === societeId

  const activeFilters = DISPLAYS.filter((d) => !mySociete || d !== 'societe')

  React.useEffect(() => {
    if (site) {
      Promise.all([
        api.controle.filtres.metier(site.id),
        api.controle.filtres.manifestation(site.id),
        api.controle.filtres.societe(site.id),
      ])
        .then(([me, ma, so]) => {
          FiltreStore.actions.setMetiers(me)
          FiltreStore.actions.setManifestations(ma)
          FiltreStore.actions.setSocietes(so)
        })
        .catch(Logger.error)
    }
  }, [site])

  React.useEffect(() => {
    setPage(0)
  }, [filters])

  React.useEffect(() => {
    if (
      controles.length < 20 &&
      pagination &&
      pagination.totalPage > 1 &&
      pagination.pageNumber < pagination.totalPage
    ) {
      setPage((prev) => prev + 1)
    }
  }, [pagination, controles])

  React.useEffect(() => {
    if (site) {
      if (!pagination || page <= pagination.totalPage) {
        setLoadingData(true)

        const params = filters.period ? MODES[filters.period] : undefined

        const request: ControleQualiteFiltreRequest = {
          dateDebut: filters.from
            ? format(new Date(filters.from), 'yyyy-MM-dd')
            : params
            ? format(new Date(params.dateDebut), 'yyyy-MM-dd')
            : undefined,
          dateFin: filters.to
            ? format(new Date(filters.to), 'yyyy-MM-dd')
            : params
            ? format(new Date(params.dateFin), 'yyyy-MM-dd')
            : undefined,
          manifestationIds: filters.manifestation.length > 0 ? filters.manifestation : undefined,
          metierIds: filters.metier.length > 0 ? filters.metier : undefined,
          societeIds: filters.societe.length > 0 ? filters.societe : undefined,
          espaceIds: espaceId ? [espaceId] : undefined,
          type: filters.type,
          nature: tab,
          observation: filters.observation,
          conforme: filters.conforme,
          siteIds: [site.id],
        }

        api.controle
          .search(request, page, `${sort},${reverse ? 'asc' : 'desc'}`)
          .then((list) => {
            if (list.page.pageNumber === 0) {
              setResults(list.controles)
            } else {
              if (
                list.controles.length === 0 &&
                list.page.totalPage > 1 &&
                list.page.pageNumber < list.page.totalPage
              ) {
                // no result but still some page to fetch, go to next page
                setPage((prev) => prev + 1)
              }

              setResults([...controles, ...list.controles])
            }

            setPagination(list.page)
          })
          .catch((err) => {
            setResults([])
            Logger.error(err)
          })
          .finally(() => setLoadingData(false))
      }
    }
  }, [filters, sort, reverse, page, tab, site])

  const links = [
    { label: i18n.t('screens.controles.title'), onClick: () => navigation.jumpTo('/controles') },
    societe
      ? {
          label: societe.nameSociete,
          onClick: () => navigation.jumpTo(`/controles/details/infos?societeId=${societeId}`),
        }
      : undefined,
    espace
      ? { label: espace.libelle, onClick: () => navigation.jumpTo(`/controles/details/infos?spaceId=${espaceId}`) }
      : undefined,
    manifestation
      ? {
          label: manifestation.libelle,
          onClick: () => navigation.jumpTo(`/controles/details/infos?manifestationId=${manifestationId}`),
        }
      : undefined,
    { label: i18n.t('screens.controles.history.title'), onClick: () => null },
  ].filter(Boolean) as HeaderLink[]

  const options = TABS.map((t) => ({ label: i18n.t(`screens.controles.form.nature.${t}`), value: t }))
  const option = options.find((opt) => opt.value === tab)

  const sortOptions = SORTS.map((key) => ({ label: i18n.t(`screens.controles.sort.${key}`), value: key }))
  const sortOption = sortOptions.find((opt) => opt.value === sort)

  const onEndReached = () => {
    if (!loadingData && pagination && pagination.totalPage > 1 && pagination.pageNumber < pagination.totalPage) {
      setPage((prev) => prev + 1)
    }
  }

  const openFilter = () => {
    Drawer.open({
      render: () => (
        <ControleFilters
          display={activeFilters}
          filtres={filters}
          onFiltre={(part) => setFilters({ ...filters, ...part })}
          onReset={() => {
            setFilters(initial)
            Drawer.close()
          }}
        />
      ),
      hideTarget: true,
    })
  }

  const renderItem = (item: ControleQualiteResponse) => {
    return (
      <Item
        key={item.id}
        activeOpacity={0.9}
        onPress={() =>
          navigation.push(`/controles/${item.id}${paramsToSearch({ espaceId, manifestationId, societeId })}`)
        }>
        <ControleCard controle={item} />
      </Item>
    )
  }

  return (
    <Root
      header={isPhone ? i18n.t('screens.controles.history.title') : undefined}
      fixed
      onLeft={navigation.goBack}
      onRight={openFilter}
      right="filter">
      {!isPhone && (
        <HeaderContainer>
          <Header
            title={i18n.t('screens.controles.title')}
            links={links}
            renderBottom={() => (
              <ControleFilters
                display={activeFilters}
                filtres={filters}
                onFiltre={(part) => setFilters({ ...filters, ...part })}
                onReset={() => setFilters(initial)}
              />
            )}
          />
        </HeaderContainer>
      )}

      <TabsContainer>
        {isPhone ? (
          <>
            <SegmentedControl data={options} onItemSelected={(opt) => setTab(opt.value)} value={option} />

            <Sort>
              <SortSelector
                sorts={sortOptions}
                sort={sortOption}
                reverse={reverse}
                onOrderChange={() => setReverse(!reverse)}
                onSortChange={(opt) => {
                  if (opt) {
                    setSort(opt.value)
                  }
                }}
              />
            </Sort>
          </>
        ) : (
          <>
            <Sort>
              <SortSelector
                sorts={sortOptions}
                sort={sortOption}
                reverse={reverse}
                onOrderChange={() => setReverse(!reverse)}
                onSortChange={(opt) => {
                  if (opt) {
                    setSort(opt.value)
                  }
                }}
              />
            </Sort>

            <Tabs data={options} value={option} onItemSelected={(opt) => setTab(opt.value)} isBig />
          </>
        )}
      </TabsContainer>

      {loadingData && page === 0 ? (
        <Loading>
          <Loader />
        </Loading>
      ) : controles.length === 0 ? (
        <MissingInfo>{i18n.t('screens.controles.history.empty')}</MissingInfo>
      ) : (
        <InfiniteList
          data={controles}
          numColumn={isPhone ? 1 : Math.floor((width - DRAWER_WIDTH) / ITEM_WIDTH)}
          height={ITEM_HEIGHT}
          renderItem={renderItem}
          onEndReached={onEndReached}
          containerStyle={{
            marginTop: 20,
            marginLeft: isPhone ? 0 : 25,
          }}
        />
      )}
    </Root>
  )
}

export default ControleHistoryListScreen

const HeaderContainer = styled(View)`
  z-index: 10;
`

const TabsContainer = styled(View)`
  justify-content: flex-start;
  padding: 0 ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 24)}px;
  padding-top: ${(props) => (props.theme.breakpoint === 'phone' ? 16 : 32)}px;
`

const Loading = styled(View)`
  flex: 1;
  align-items: center;
  justify-content: center;
`

const MissingInfo = styled(Text)`
  ${(props) => props.theme.fonts.Footnote};
  font-size: 14px;
  color: ${(props) => props.theme.colors.secondaryText};
  ${(props) =>
    props.theme.breakpoint === 'phone'
      ? ` padding: 23px 16px;
          background-color: ${props.theme.colors.background};
          text-align: center;
          font-size: 12px;`
      : `padding: 16px;`};
  margin: 16px;
`

const Item = styled(TouchableOpacity)`
  ${(props) => (props.theme.breakpoint === 'phone' ? 'margin: 0px 16px' : 'margin-right: 15px')};
`

const Sort = styled(View)`
  align-items: flex-start;
  ${(props) => (props.theme.breakpoint === 'phone' ? 'margin-top: 16px' : 'margin-bottom: 16px')};
  margin-right: ${(props) => (props.theme.breakpoint === 'phone' ? 8 : 32)}px;
`
